import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Calendar, Trash2, Check, X } from 'lucide-react';

import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card } from '../ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { useAuth, API } from '../../contexts/AuthContext';
import { getErrorMessage } from '../../utils/errorHandler';

const ReservationsTab = () => {
  const { getAuthHeaders } = useAuth();
  const [reservations, setReservations] = useState([]);
  const [tables, setTables] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    table_id: '',
    customer_name: '',
    customer_phone: '',
    reservation_date: '',
    party_size: 2
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadReservations();
    loadTables();
  }, []);

  const loadReservations = async () => {
    try {
      const response = await axios.get(`${API}/reservations`, {
        headers: getAuthHeaders()
      });
      setReservations(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Error al cargar reservaciones'));
    }
  };

  const loadTables = async () => {
    try {
      const response = await axios.get(`${API}/tables`, {
        headers: getAuthHeaders()
      });
      setTables(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Error al cargar mesas'));
    }
  };

  const handleOpenDialog = () => {
    setFormData({
      table_id: '',
      customer_name: '',
      customer_phone: '',
      reservation_date: '',
      party_size: 2
    });
    setIsDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/reservations`, formData, {
        headers: getAuthHeaders()
      });
      toast.success('Reservación creada exitosamente');
      await loadReservations();
      handleCloseDialog();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Error al crear reservación'));
    }
    setLoading(false);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setTimeout(() => {
      setFormData({
        table_id: '',
        customer_name: '',
        customer_phone: '',
        reservation_date: '',
        party_size: 2
      });
    }, 100);
  };

  const handleUpdateStatus = async (reservationId, newStatus) => {
    try {
      await axios.patch(
        `${API}/reservations/${reservationId}/status`,
        { status: newStatus },
        { headers: getAuthHeaders() }
      );
      // toast.success(`Reservación ${newStatus === 'confirmed' ? 'confirmada' : newStatus === 'cancelled' ? 'cancelada' : 'completada'}`);
      loadReservations();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Error al actualizar estado'));
    }
  };

  const handleDelete = async (reservationId) => {
    if (!window.confirm('¿Estás seguro de eliminar esta reservación?')) return;

    try {
      await axios.delete(`${API}/reservations/${reservationId}`, {
        headers: getAuthHeaders()
      });
      toast.success('Reservación eliminada exitosamente');
      loadReservations();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Error al eliminar reservación'));
    }
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'confirmed': return 'bg-green-100 text-green-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      case 'completed': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status) => {
    switch(status) {
      case 'pending': return 'Pendiente';
      case 'confirmed': return 'Confirmada';
      case 'cancelled': return 'Cancelada';
      case 'completed': return 'Completada';
      default: return status;
    }
  };

  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="bg-white/80 backdrop-blur-xl rounded-2xl p-6 border border-orange-100">
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-red-500 rounded-xl flex items-center justify-center">
            <Calendar className="w-6 h-6 text-white" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-gray-800">Reservaciones</h2>
            <p className="text-sm text-gray-600">Gestiona las reservas de mesas</p>
          </div>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={handleOpenDialog} className="bg-gradient-to-r from-orange-500 to-red-500 rounded-xl">
              <Plus className="w-4 h-4 mr-2" />
              Nueva Reservación
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Nueva Reservación</DialogTitle>
              <DialogDescription>
                Completa la información para crear una nueva reservación de mesa
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label>Mesa</Label>
                <Select value={formData.table_id} onValueChange={(value) => setFormData({ ...formData, table_id: value })} required>
                  <SelectTrigger className="rounded-xl">
                    <SelectValue placeholder="Selecciona una mesa" />
                  </SelectTrigger>
                  <SelectContent>
                    {tables.filter(t => t.status === 'free' || t.status === 'available').map(table => (
                      <SelectItem key={table.id} value={table.id}>
                        {table.name || `Mesa ${table.number}`} (Cap: {table.capacity})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Nombre del Cliente</Label>
                <Input
                  value={formData.customer_name}
                  onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })}
                  placeholder="Nombre completo"
                  required
                  className="rounded-xl"
                />
              </div>
              <div>
                <Label>Teléfono del Cliente</Label>
                <Input
                  value={formData.customer_phone}
                  onChange={(e) => setFormData({ ...formData, customer_phone: e.target.value })}
                  placeholder="Número de teléfono"
                  required
                  className="rounded-xl"
                />
              </div>
              <div>
                <Label>Fecha y Hora de Reservación</Label>
                <Input
                  type="datetime-local"
                  value={formData.reservation_date}
                  onChange={(e) => setFormData({ ...formData, reservation_date: e.target.value })}
                  required
                  className="rounded-xl"
                />
              </div>
              <div>
                <Label>Número de Personas</Label>
                <Input
                  type="number"
                  min="1"
                  value={formData.party_size}
                  onChange={(e) => setFormData({ ...formData, party_size: parseInt(e.target.value) })}
                  required
                  className="rounded-xl"
                />
              </div>
              <div className="flex gap-2">
                <Button type="button" variant="outline" onClick={handleCloseDialog} className="flex-1 rounded-xl">
                  Cancelar
                </Button>
                <Button type="submit" disabled={loading} className="flex-1 bg-gradient-to-r from-orange-500 to-red-500 rounded-xl">
                  {loading ? 'Creando...' : 'Crear Reservación'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {reservations.length === 0 ? (
        <div className="text-center py-12">
          <Calendar className="w-16 h-16 mx-auto text-gray-300 mb-4" />
          <p className="text-gray-500">No hay reservaciones registradas</p>
        </div>
      ) : (
        <div className="grid gap-4">
          {reservations.map((reservation) => {
            const table = tables.find(t => t.id === reservation.table_id);
            return (
              <Card key={reservation.id} className="p-4 hover:shadow-lg transition-shadow">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="font-bold text-lg text-gray-800">{reservation.customer_name}</h3>
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(reservation.status)}`}>
                        {getStatusText(reservation.status)}
                      </span>
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-sm text-gray-600">
                      <div>
                        <span className="font-medium">Mesa:</span> {table?.name || `Mesa ${table?.number}`}
                      </div>
                      <div>
                        <span className="font-medium">Personas:</span> {reservation.party_size}
                      </div>
                      <div>
                        <span className="font-medium">Teléfono:</span> {reservation.customer_phone}
                      </div>
                      <div>
                        <span className="font-medium">Fecha:</span> {formatDateTime(reservation.reservation_date)}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {reservation.status === 'pending' && (
                      <Button 
                        size="sm" 
                        variant="outline" 
                        onClick={() => handleUpdateStatus(reservation.id, 'confirmed')} 
                        className="rounded-xl text-green-600 hover:bg-green-50"
                      >
                        <Check className="w-4 h-4" />
                      </Button>
                    )}
                    {(reservation.status === 'pending' || reservation.status === 'confirmed') && (
                      <Button 
                        size="sm" 
                        variant="outline" 
                        onClick={() => handleUpdateStatus(reservation.id, 'cancelled')} 
                        className="rounded-xl text-red-600 hover:bg-red-50"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    )}
                    <Button 
                      size="sm" 
                      variant="outline" 
                      onClick={() => handleDelete(reservation.id)} 
                      className="rounded-xl text-red-600 hover:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ReservationsTab;
